import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { adminAPI } from '../services/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface SidebarSettings {
  isVisible: boolean;
  itemOrder: string[];
  isEditMode: boolean;
}

interface SidebarContextType {
  settings: SidebarSettings;
  toggleSidebar: () => void;
  toggleEditMode: () => void;
  reorderItems: (newOrder: string[]) => void;
  resetToDefault: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const defaultNavigationOrder = [
  'Dashboard',
  'Users',
  'Bookings',
  'Manual Leads',
  'Services',
  'Professionals',
  'Worker Approvals',
  'Hubs',
  'Service Categories',
  'Blog Posts',
  'Blog Categories',
  'Slideshow',
  'Coupons',
  'Customer Complaints',
  'Reviews',
  'Customer Ratings',
  'Approvals',
  'Notifications',
  'Analytics',
  'Payments',
  'Referrals',
  'Audit Logs',
  'Admin Access'
];

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  
  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem('admin_token');
  
  // Fetch admin preferences from backend only if authenticated
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['admin-preferences'],
    queryFn: () => adminAPI.getAdminPreferences(),
    enabled: isAuthenticated, // Only run query if authenticated
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation for updating sidebar preferences
  const updateSidebarMutation = useMutation({
    mutationFn: (sidebar: { isVisible: boolean; itemOrder: string[] }) => 
      adminAPI.updateSidebarPreferences(sidebar),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-preferences'] });
    },
  });

  const [settings, setSettings] = useState<SidebarSettings>(() => {
    // 1. Try to load from localStorage for immediate UI response (fastest)
    const cached = localStorage.getItem('sidebar_settings');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return {
          ...parsed,
          isVisible: false, // Force hidden on first load as requested
          isEditMode: false
        };
      } catch (e) {
        console.error('Failed to parse cached sidebar settings', e);
      }
    }
    
    // 2. Default values if no cache
    return {
      isVisible: false, // Default to hidden as requested
      itemOrder: defaultNavigationOrder,
      isEditMode: false
    };
  });

  // Update settings when preferences are loaded from backend
  useEffect(() => {
    if (preferences?.data?.sidebar) {
      const newSettings = {
        isVisible: preferences.data.sidebar.isVisible ?? false,
        itemOrder: preferences.data.sidebar.itemOrder ?? defaultNavigationOrder,
        isEditMode: false
      };
      setSettings(newSettings);
      // Update cache
      localStorage.setItem('sidebar_settings', JSON.stringify(newSettings));
    }
  }, [preferences]);

  const toggleSidebar = () => {
    const newVisibility = !settings.isVisible;
    const newSettings = {
      ...settings,
      isVisible: newVisibility
    };
    setSettings(newSettings);
    localStorage.setItem('sidebar_settings', JSON.stringify(newSettings));
    
    // Save to backend
    updateSidebarMutation.mutate({
      isVisible: newVisibility,
      itemOrder: settings.itemOrder
    });
  };

  const toggleEditMode = () => {
    setSettings(prev => {
      const newEditMode = !prev.isEditMode;
      const newVisibility = newEditMode ? true : prev.isVisible;
      
      const newSettings = {
        ...prev,
        isEditMode: newEditMode,
        isVisible: newVisibility
      };
      
      // Update cache
      localStorage.setItem('sidebar_settings', JSON.stringify({
        isVisible: newVisibility,
        itemOrder: prev.itemOrder
      }));

      // Save to backend if visibility changed
      if (newVisibility !== prev.isVisible) {
        updateSidebarMutation.mutate({
          isVisible: newVisibility,
          itemOrder: prev.itemOrder
        });
      }
      
      return newSettings;
    });
  };

  const reorderItems = (newOrder: string[]) => {
    const newSettings = {
      ...settings,
      itemOrder: newOrder
    };
    setSettings(newSettings);
    localStorage.setItem('sidebar_settings', JSON.stringify(newSettings));
    
    // Save to backend
    updateSidebarMutation.mutate({
      isVisible: settings.isVisible,
      itemOrder: newOrder
    });
  };

  const resetToDefault = () => {
    setSettings(prev => ({
      ...prev,
      itemOrder: defaultNavigationOrder,
      // Keep edit mode on so user can continue editing
    }));
    
    // Save to backend
    updateSidebarMutation.mutate({
      isVisible: settings.isVisible,
      itemOrder: defaultNavigationOrder
    });
  };

  return (
    <SidebarContext.Provider value={{
      settings,
      toggleSidebar,
      toggleEditMode,
      reorderItems,
      resetToDefault
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};